/**
 * Order History Component
 */

import { useOrders } from '@/hooks';
import { useShop } from '@/context';
import { Spinner } from '../shared/Spinner';
import { Button } from '../shared/Button';
import type { Order, OrderStatus } from '@/types';
import styles from './OrderHistory.module.css';

export function OrderHistory() {
  const { orders: rawOrders, isLoading, pages, currentPage, goToPage } = useOrders();
  const { formatPrice, t } = useShop();

  // Ensure orders is always an array
  const orders = rawOrders ?? [];

  if (isLoading && orders.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className={styles.empty}>
        <p>{t('noOrders')}</p>
        <Button
          variant="primary"
          onClick={() => (window.location.href = window.evcShop.urls.shop)}
        >
          Découvrir nos produits
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('myOrders')}</h2>

      <div className={styles.orders}>
        {orders.map((order) => (
          <OrderCard key={order.id} order={order} formatPrice={formatPrice} />
        ))}
      </div>

      {pages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="small"
            disabled={currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            Précédent
          </Button>
          <span className={styles.pageInfo}>
            Page {currentPage} sur {pages}
          </span>
          <Button
            variant="outline"
            size="small"
            disabled={currentPage >= pages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}

const META_LABELS: Record<string, string> = {
  option_name: 'Option',
  initials: 'Initiales',
  names: 'Prénoms',
  date: 'Date',
  message: 'Message',
  color: 'Couleur',
  font: 'Police',
  location: 'Emplacement',
};

function ItemMeta({ meta }: { meta: Record<string, any> }) {
  // New format: meta.options is an array of option objects
  if (meta.options && Array.isArray(meta.options)) {
    return (
      <div className={styles.itemMetaDetails}>
        {meta.options.map((opt: any, idx: number) => {
          const entries = Object.entries(opt).filter(
            ([key, val]) => val && key in META_LABELS
          );
          return entries.map(([key, val]) => (
            <span key={`${idx}-${key}`} className={styles.itemMeta}>
              {META_LABELS[key]}: {String(val)}
            </span>
          ));
        })}
      </div>
    );
  }

  // Legacy format: flat meta object
  const entries = Object.entries(meta).filter(
    ([key, val]) => val && key in META_LABELS
  );
  if (entries.length === 0) return null;

  return (
    <div className={styles.itemMetaDetails}>
      {entries.map(([key, val]) => (
        <span key={key} className={styles.itemMeta}>
          {META_LABELS[key]}: {String(val)}
        </span>
      ))}
    </div>
  );
}

interface OrderCardProps {
  order: Order;
  formatPrice: (amount: number) => string;
}

function OrderCard({ order, formatPrice }: OrderCardProps) {
  const statusLabels: Record<OrderStatus, string> = {
    pending: 'En attente',
    paid: 'Payée',
    processing: 'En préparation',
    shipped: 'Expédiée',
    delivered: 'Livrée',
    cancelled: 'Annulée',
    refunded: 'Remboursée',
  };

  const statusColors: Record<OrderStatus, string> = {
    pending: styles.statusPending,
    paid: styles.statusPaid,
    processing: styles.statusProcessing,
    shipped: styles.statusShipped,
    delivered: styles.statusDelivered,
    cancelled: styles.statusCancelled,
    refunded: styles.statusRefunded,
  };

  const apiOrder = order as Order & {
    date_display?: string;
    shipping_label?: string;
    shipping_estimated_delivery?: string;
    shipping_cost?: number;
  };

  const date = apiOrder.date_display
    || new Date(order.date_created).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  return (
    <div className={styles.order}>
      <div className={styles.orderHeader}>
        <div className={styles.orderInfo}>
          <span className={styles.orderNumber}>
            Commande {order.order_number}
          </span>
          <span className={styles.orderDate}>{date}</span>
        </div>
        <span className={`${styles.status} ${statusColors[order.status]}`}>
          {statusLabels[order.status]}
        </span>
      </div>

      <div className={styles.orderItems}>
        {order.items.slice(0, 3).map((item) => {
          const meta = (item as any).meta as Record<string, string> | null;
          const variation = (item as any).variation as Record<string, string> | null;
          return (
            <div key={item.id} className={styles.orderItem}>
              {item.image && (
                <img src={item.image} alt={item.name} className={styles.itemImage} />
              )}
              <div className={styles.itemDetails}>
                <span className={styles.itemName}>{item.name}</span>
                <span className={styles.itemQty}>Qté: {item.quantity}</span>
                {variation && Object.keys(variation).length > 0 && (
                  <span className={styles.itemMeta}>
                    {Object.entries(variation).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                  </span>
                )}
                {meta && Object.keys(meta).length > 0 && (
                  <ItemMeta meta={meta} />
                )}
              </div>
            </div>
          );
        })}
        {order.items.length > 3 && (
          <span className={styles.moreItems}>
            +{order.items.length - 3} autres articles
          </span>
        )}
      </div>

      {(apiOrder.shipping_label || apiOrder.shipping_estimated_delivery) && (
        <div className={styles.shippingInfo}>
          {apiOrder.shipping_label && (
            <span className={styles.shippingLabel}>
              {apiOrder.shipping_label}
              {apiOrder.shipping_cost ? ` — ${formatPrice(apiOrder.shipping_cost)}` : ''}
            </span>
          )}
          {apiOrder.shipping_estimated_delivery && (
            <span className={styles.shippingDelivery}>
              Livraison estimée : {apiOrder.shipping_estimated_delivery}
            </span>
          )}
        </div>
      )}

      <div className={styles.orderFooter}>
        <span className={styles.orderTotal}>
          Total: <strong>{formatPrice(order.total)}</strong>
        </span>
        {order.tracking_url && (
          <a
            href={order.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.trackingLink}
          >
            Suivre mon colis
          </a>
        )}
      </div>
    </div>
  );
}
